/**
 * AI Review Mode
 * 
 * Platform Principle 15:
 * "AI Assists. People Decide."
 * 
 * No information is automatically committed. 
 * AI proposes. Humans approve.
 * 
 * Features:
 * - Display extracted fields with confidence scores
 * - Show WHERE each field came from (source attribution)
 * - Highlight AI-suggested values vs empty fields
 * - Allow user to edit, approve, or reject each field
 * - Final "Confirm & Save" action
 * 
 * Source Attribution shows:
 * - Document name, page number, section
 * - Input method (typed, spoken, pasted, scanned)
 * - AI reasoning for extraction
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Badge } from './badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { 
  Check, X, Edit2, AlertCircle, CheckCircle2, Info, 
  ChevronDown, ChevronUp, FileText, Mic, ClipboardPaste,
  Camera, Upload, Sparkles, Shield, Loader2, RotateCcw
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Confidence level colors
const CONFIDENCE_COLORS = {
  high: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-red-100 text-red-800 border-red-200',
};

const CONFIDENCE_ICONS = {
  high: CheckCircle2,
  medium: AlertCircle,
  low: AlertCircle,
};

// Input method icons
const INPUT_METHOD_ICONS = {
  manual: Edit2,
  natural_language: FileText,
  voice: Mic,
  paste: ClipboardPaste,
  document: FileText,
  image: Camera,
  camera: Camera,
  email: FileText,
  multi_source: Sparkles,
};

// Field component with source attribution
function ExtractedFieldCard({ 
  field, 
  onApprove, 
  onReject, 
  onEdit, 
  isEditing, 
  editValue, 
  onEditChange,
  onEditSave,
  onEditCancel,
}) {
  const [showSource, setShowSource] = useState(false);
  
  const ConfidenceIcon = CONFIDENCE_ICONS[field.confidence_level] || AlertCircle;
  const InputIcon = INPUT_METHOD_ICONS[field.source?.input_method] || FileText;
  
  const confidencePercent = Math.round(field.confidence * 100);
  
  return (
    <Card className={cn(
      "transition-all",
      field.approved && "border-green-300 bg-green-50/50",
      field.rejected && "border-red-300 bg-red-50/50 opacity-50"
    )}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Field name and confidence badge */}
            <div className="flex items-center gap-2 mb-2">
              <Label className="font-medium text-sm capitalize">
                {field.field_name.replace(/_/g, ' ')}
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge 
                      variant="outline" 
                      className={cn("text-xs", CONFIDENCE_COLORS[field.confidence_level])}
                    >
                      <ConfidenceIcon className="h-3 w-3 mr-1" />
                      {confidencePercent}%
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>AI confidence: {field.confidence_level}</p>
                    {field.ai_reasoning && (
                      <p className="text-xs mt-1">Reason: {field.ai_reasoning}</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            {/* Field value */}
            {isEditing ? (
              <div className="flex gap-2">
                <Input
                  value={editValue}
                  onChange={(e) => onEditChange(e.target.value)}
                  className="flex-1"
                  autoFocus
                />
                <Button size="sm" variant="ghost" onClick={onEditSave}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={onEditCancel}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="text-sm">
                {Array.isArray(field.value) ? (
                  <div className="flex flex-wrap gap-1">
                    {field.value.map((item, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {item}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className={cn(
                    "font-medium",
                    field.needs_review && "text-amber-700"
                  )}>
                    {field.value?.toString() || '—'}
                  </span>
                )}
              </div>
            )}
            
            {/* Source attribution toggle */}
            <Collapsible open={showSource} onOpenChange={setShowSource}>
              <CollapsibleTrigger asChild>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 mt-2 text-xs text-muted-foreground"
                >
                  <InputIcon className="h-3 w-3 mr-1" />
                  View source
                  {showSource ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2 p-2 bg-muted/50 rounded text-xs space-y-1">
                  {field.source && (
                    <>
                      <p><strong>Source:</strong> {field.source.source_name || 'Direct input'}</p>
                      <p><strong>Method:</strong> {field.source.input_method?.replace(/_/g, ' ')}</p>
                      {field.source.page_number && (
                        <p><strong>Page:</strong> {field.source.page_number}</p>
                      )}
                      {field.source.section && (
                        <p><strong>Section:</strong> {field.source.section}</p>
                      )}
                    </>
                  )}
                  {field.source_text && (
                    <p className="italic text-muted-foreground mt-1">
                      &ldquo;{field.source_text}&rdquo;
                    </p>
                  )}
                  {field.ai_reasoning && (
                    <p><strong>AI reasoning:</strong> {field.ai_reasoning}</p>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          
          {/* Action buttons */}
          {!field.approved && !field.rejected && !isEditing && (
            <div className="flex gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={onEdit}
                      data-testid={`edit-field-${field.field_name}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit value</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-green-600 hover:text-green-700 hover:bg-green-100"
                      onClick={onApprove}
                      data-testid={`approve-field-${field.field_name}`}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Approve</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-100"
                      onClick={onReject}
                      data-testid={`reject-field-${field.field_name}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Reject</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          
          {/* Status indicator */}
          {field.approved && (
            <Badge className="bg-green-100 text-green-700 border-green-200">
              <Check className="h-3 w-3 mr-1" />
              Approved
            </Badge>
          )}
          {field.rejected && (
            <Badge className="bg-red-100 text-red-700 border-red-200">
              <X className="h-3 w-3 mr-1" />
              Rejected
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function AIReviewMode({
  extractionResult,
  onConfirm,
  onCancel,
  isLoading = false,
  entityType,
  existingData = null,
  className,
}) {
  // Transform fields into stateful objects
  const [fields, setFields] = useState(() => {
    if (!extractionResult?.fields) return [];
    return extractionResult.fields.map(f => ({
      ...f,
      approved: false,
      rejected: false,
      edited: false,
      editedValue: null,
    }));
  });
  
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [corrections, setCorrections] = useState([]);
  
  // Update fields when extractionResult changes
  useEffect(() => {
    if (extractionResult?.fields) {
      setFields(extractionResult.fields.map(f => ({
        ...f,
        approved: false,
        rejected: false,
        edited: false,
        editedValue: null,
      })));
    }
  }, [extractionResult]);
  
  // Approve a field
  const handleApprove = useCallback((fieldName) => {
    setFields(prev => prev.map(f => 
      f.field_name === fieldName 
        ? { ...f, approved: true, rejected: false }
        : f
    ));
  }, []);
  
  // Reject a field
  const handleReject = useCallback((fieldName) => {
    setFields(prev => prev.map(f => 
      f.field_name === fieldName 
        ? { ...f, rejected: true, approved: false }
        : f
    ));
  }, []);
  
  // Start editing a field
  const handleEdit = useCallback((field) => {
    setEditingField(field.field_name);
    setEditValue(Array.isArray(field.value) ? field.value.join(', ') : field.value?.toString() || '');
  }, []);
  
  // Save edited value
  const handleEditSave = useCallback(() => {
    if (!editingField) return;
    
    const originalField = fields.find(f => f.field_name === editingField);
    const originalValue = originalField?.value;
    
    // Track correction for learning
    if (originalValue !== editValue) {
      setCorrections(prev => [...prev, {
        field_name: editingField,
        original_value: originalValue,
        corrected_value: editValue,
        reason: 'User correction',
      }]);
    }
    
    setFields(prev => prev.map(f => 
      f.field_name === editingField 
        ? { 
            ...f, 
            value: editValue, 
            edited: true, 
            editedValue: editValue,
            approved: true,
            rejected: false,
          }
        : f
    ));
    
    setEditingField(null);
    setEditValue('');
  }, [editingField, editValue, fields]);
  
  // Cancel editing
  const handleEditCancel = useCallback(() => {
    setEditingField(null);
    setEditValue('');
  }, []);
  
  // Approve all remaining fields
  const handleApproveAll = useCallback(() => {
    setFields(prev => prev.map(f => 
      !f.approved && !f.rejected 
        ? { ...f, approved: true }
        : f
    ));
  }, []);
  
  // Reset all decisions
  const handleReset = useCallback(() => {
    setFields(prev => prev.map(f => ({
      ...f,
      approved: false,
      rejected: false,
      edited: false,
      editedValue: null,
    })));
    setCorrections([]);
  }, []);
  
  // Confirm and save
  const handleConfirm = useCallback(async () => {
    const approvedData = {};
    const approvedFields = fields.filter(f => f.approved);
    
    for (const field of approvedFields) {
      approvedData[field.field_name] = field.edited ? field.editedValue : field.value;
    }
    
    await onConfirm({
      approved_data: approvedData,
      corrections: corrections,
      extraction_id: extractionResult?.extraction_id,
    });
  }, [fields, corrections, extractionResult, onConfirm]);
  
  // Calculate summary stats
  const stats = {
    total: fields.length,
    approved: fields.filter(f => f.approved).length,
    rejected: fields.filter(f => f.rejected).length,
    pending: fields.filter(f => !f.approved && !f.rejected).length,
    highConfidence: fields.filter(f => f.confidence_level === 'high').length,
    needsReview: fields.filter(f => f.needs_review).length,
  };
  
  const canConfirm = stats.approved > 0 && stats.pending === 0;
  
  // Group fields by category
  const requiredFields = fields.filter(f => extractionResult?.required_fields?.includes(f.field_name));
  const optionalFields = fields.filter(f => !extractionResult?.required_fields?.includes(f.field_name));
  
  if (!extractionResult || !fields.length) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No extracted data to review</p>
      </div>
    );
  }
  
  return (
    <div className={cn("space-y-6", className)} data-testid="ai-review-mode">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                AI Review Mode
              </CardTitle>
              <CardDescription>
                AI Assists. You Decide. Review and approve each field before saving.
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">
              {Math.round(extractionResult.summary?.overall_confidence * 100 || 0)}% overall confidence
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary stats */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <span>{stats.approved} approved</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <span>{stats.rejected} rejected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-amber-500" />
              <span>{stats.pending} pending</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span>{stats.needsReview} need review</span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-500 transition-all"
              style={{ width: `${(stats.approved / stats.total) * 100}%` }}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-between border-t pt-4">
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset All
          </Button>
          <Button variant="outline" size="sm" onClick={handleApproveAll}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Approve All Pending
          </Button>
        </CardFooter>
      </Card>
      
      {/* Sources used */}
      {extractionResult.sources_used?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-muted-foreground">Sources:</span>
          {extractionResult.sources_used.map((source, i) => {
            const Icon = INPUT_METHOD_ICONS[source.input_method] || FileText;
            return (
              <Badge key={i} variant="secondary" className="text-xs">
                <Icon className="h-3 w-3 mr-1" />
                {source.source_name || source.input_method}
              </Badge>
            );
          })}
        </div>
      )}
      
      {/* Warnings */}
      {extractionResult.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
            <AlertCircle className="h-4 w-4" />
            Attention Required
          </h4>
          <ul className="text-sm text-amber-700 space-y-1">
            {extractionResult.warnings.map((warning, i) => (
              <li key={i}>• {warning}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Fields grid */}
      <div className="grid gap-3 md:grid-cols-2">
        {fields.map((field) => (
          <ExtractedFieldCard
            key={field.field_name}
            field={field}
            onApprove={() => handleApprove(field.field_name)}
            onReject={() => handleReject(field.field_name)}
            onEdit={() => handleEdit(field)}
            isEditing={editingField === field.field_name}
            editValue={editValue}
            onEditChange={setEditValue}
            onEditSave={handleEditSave}
            onEditCancel={handleEditCancel}
          />
        ))}
      </div>
      
      {/* Action buttons */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        
        <div className="flex items-center gap-4">
          {corrections.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {corrections.length} correction(s) logged for learning
            </span>
          )}
          
          <Button 
            onClick={handleConfirm}
            disabled={!canConfirm || isLoading}
            data-testid="confirm-save-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirm & Save ({stats.approved} fields)
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Footer note */}
      <p className="text-xs text-muted-foreground text-center">
        <Sparkles className="h-3 w-3 inline mr-1" />
        AI proposed this data. You approved it. Full audit trail recorded.
      </p>
    </div>
  );
}

export default AIReviewMode;
